import {
  WorkplaceRequestReservationAttendeeInput,
  WorkplaceRequestUser
} from "./workplace-requests";

export type WorkplaceRequestTypeSummary = {
  id: string;
  name: string;
};

/**
 * Base status a sub-status (ticket type status) maps to. Mirrors the backend
 * `base_status` enum used for ticket types statuses and reordering.
 */
export type WorkplaceRequestBaseStatus = "open" | "closed" | "pending";

export type WorkplaceRequestTypeFieldType =
  | "short_text"
  | "long_text"
  | "single_select"
  | "multi_select"
  | "toggle"
  | "number"
  | "date"
  /**
   * A time of day with no calendar day attached. Stored and returned as a
   * 24-hour `HH:MM` string, with no timezone: core rejects anything else.
   */
  | "time"
  | "file_upload"
  | "person"
  | "seat"
  /**
   * Display-only prose shown inside a form. Carries no answer: the backend
   * rejects a `field_value` for one, refuses `is_required`, and takes no
   * options. Its body lives in `description`.
   */
  | "info_block"
  /**
   * A booking the submitter describes but that nothing books yet: one or more
   * days sharing a start and end time, plus — where the field's
   * {@link WorkplaceRequestReservationFieldConfig} allows it — a recurrence and
   * a list of attendees. The answer is stored on the ticket as
   * `WorkplaceRequestReservationFieldValue`; a `reservations:create` workflow
   * turns it into real reservations when the request is approved.
   */
  | "reservation";

/**
 * What a request type *is*. `default` renders a form and files a ticket;
 * `information` replaces the form with read-only instructions and files
 * nothing — approval, assignment, statuses, workflows and notifications do not
 * apply to it.
 *
 * Only `information → default` is a legal transition (the "bring into Tactic as
 * a form" migration). The reverse is rejected: a form may already have tickets
 * filed against it, and an information type has no ticket relation to hold them.
 */
export type WorkplaceRequestTypeKind = "default" | "information";

/**
 * Whether a request type may (or must) have a schedule/reservation linked.
 * Mirrors the backend `schedule_requirement` enum.
 */
export type WorkplaceRequestScheduleRequirement =
  | "optional"
  | "not_allowed"
  | "required";

export type WorkplaceRequestTypeFieldOption = {
  id: string;
  label: string;
  /** Secondary line shown under the option's label. */
  description?: string;
  /**
   * Signed, time-limited URL for the option's image. Absent (not null) when the
   * option has no image. Only ever set for `single_select` / `multi_select`.
   */
  image_url?: string;
};

/** Image formats accepted by the ticket-type option image upload endpoint. */
export type WorkplaceRequestTypeImageFormat = "png" | "jpg" | "jpeg" | "webp";

/**
 * What a `reservation` field lets the submitter fill in.
 *
 * All three switches are required: core stores them together and rejects a
 * partial config. Turning one off never rewrites answers already submitted
 * with it on — it only stops new ones from carrying that part.
 */
export type WorkplaceRequestReservationFieldConfig = {
  /** Whether the submitter may repeat the booking. */
  recurrence_enabled: boolean;
  /** Whether the submitter may invite attendees. */
  attendees_enabled: boolean;
  /**
   * Whether the submitter may pick several days for one answer. Every day
   * shares the same start and end time and becomes its own reservation, so an
   * answer carries several days *or* a recurrence, never both.
   */
  multi_day_enabled: boolean;
};

/**
 * Per-field configuration. Only the entry matching the field's own
 * `field_type` may be set — sending a `reservation` entry on any other type is
 * a 400 `invalid_field_config`.
 */
export type WorkplaceRequestTypeFieldConfig = {
  reservation?: WorkplaceRequestReservationFieldConfig;
};

/**
 * A configurable field belonging to a workplace request type (the "form").
 */
export type WorkplaceRequestTypeField = {
  id: string;
  name: string;
  /**
   * Help text under the input — except on an `info_block`, where it *is* the
   * content: sanitized HTML up to 20 000 characters (every other type is
   * capped at 500).
   */
  description?: string;
  field_type: WorkplaceRequestTypeFieldType;
  is_required: boolean;
  options?: WorkplaceRequestTypeFieldOption[];
  /** Set only on the field types that take configuration (`reservation`). */
  config?: WorkplaceRequestTypeFieldConfig;
};

// ---------------------------------------------------------------------------
// Mock-only domain types — not yet backed by the API.
// See docs/workplace-requests-backend-api-gaps.md (§4 Linked Workflows, §5 Notifications).
// ---------------------------------------------------------------------------

export type WorkplaceRequestNotificationTrigger =
  | "ticket_created"
  | "ticket_assigned"
  | "status_changed";

export type WorkplaceRequestNotificationRecipient =
  | { kind: "submitter" }
  | { kind: "assignee" }
  | { kind: "user"; user_id: string }
  | { kind: "category"; category_id: string; office_id: string | null };

export type WorkplaceRequestNotificationRule = {
  id: string;
  trigger: WorkplaceRequestNotificationTrigger;
  recipients: WorkplaceRequestNotificationRecipient[];
};

// ---------------------------------------------------------------------------
// Child-ticket field prefills.
// The shape the Linked Workflows tab edits, mapped to/from the workflow API's
// `field_mappings` at the UI boundary (see the tab's `workflow-model.ts`).
// ---------------------------------------------------------------------------

/**
 * How a sub-ticket field gets its value when the linked workflow spawns it:
 * - `assignee`: left empty; the sub-ticket assignee fills it in later.
 * - `fixed`: a fixed value chosen at configuration time.
 * - `copy_from_parent`: copied from a field on the parent ticket.
 */
export type WorkplaceRequestFieldPrefill =
  | { field_id: string; kind: "assignee" }
  | { field_id: string; kind: "fixed"; value: unknown }
  | { field_id: string; kind: "copy_from_parent"; parent_field_id: string };

// ---------------------------------------------------------------------------
// Ticket type workflows — real API.
// core: `/v1/tickets/types/{type_id}/workflows` (create/update/delete) and
// embedded under each ticket type's `workflows` array on the list endpoint.
// A workflow runs an `action` when its `trigger` event fires for a ticket of
// the owning type. See docs/workplace-requests-backend-api-gaps.md §4.
// ---------------------------------------------------------------------------

/**
 * Trigger events a workflow can listen to.
 *
 * `tickets:ticket.approved` is the only one core implements. A rule that should
 * wait for a date still uses it, and defers the action with the config's
 * reserved `run_at` key — see {@link WorkplaceRequestWorkflowRunAt}.
 */
export const WORKPLACE_REQUEST_WORKFLOW_TRIGGERS = {
  approved: "tickets:ticket.approved"
} as const;

export type WorkplaceRequestWorkflowTrigger =
  (typeof WORKPLACE_REQUEST_WORKFLOW_TRIGGERS)[keyof typeof WORKPLACE_REQUEST_WORKFLOW_TRIGGERS];

/**
 * Actions a workflow can perform.
 *
 * The four `resources:` actions come in an assign/release pair per hold kind,
 * and all share {@link WorkplaceRequestResourceAssignmentConfig} — a release
 * names the desk it frees just like an assignment does.
 */
export const WORKPLACE_REQUEST_WORKFLOW_ACTIONS = {
  createChildTicket: "tickets:create_child_ticket",
  /** Permanent primary assignment — surfaced as "Assigned" in the UI. */
  setPrimaryAssignment: "resources:set_primary_assignment",
  /** Dedicated user — surfaced as "Dedicated"; the resource must be dedicated. */
  assignDedicatedUser: "resources:assign_dedicated_user",
  /** Releases the dedicated hold the person has on that desk. */
  unassignDedicatedUser: "resources:unassign_dedicated_user",
  /** Drops every primary assignment the person has on that desk. */
  removePrimaryAssignment: "resources:remove_primary_assignment",
  /** POSTs a signed event to a configured URL. */
  sendWebhook: "webhooks:send",
  /** Books one reservation from the ticket, editable by the approver. */
  createReservation: "reservations:create"
} as const;

export type WorkplaceRequestWorkflowAction =
  (typeof WORKPLACE_REQUEST_WORKFLOW_ACTIONS)[keyof typeof WORKPLACE_REQUEST_WORKFLOW_ACTIONS];

/**
 * Context keys resolved from the approved ticket at run time. Field-backed keys
 * are built with {@link workplaceRequestTicketFieldContextKey}: one per
 * `person` (user), `seat` (resource), `date` (date) and `time` (time) field on
 * the type.
 *
 * A `reservation` field is the exception: it publishes no value under its own
 * bare key, only the parts listed in
 * {@link WORKPLACE_REQUEST_RESERVATION_CONTEXT_PARTS}.
 */
export const WORKPLACE_REQUEST_WORKFLOW_CONTEXT_KEYS = {
  submitter: "ticket.submitter",
  owner: "ticket.owner",
  /** Resolves only when the ticket's location is a resource, not an office. */
  resourceLocation: "ticket.resource_location"
} as const;

const TICKET_FIELD_KEY_PREFIX = "ticket.field.";

/** The context key that yields the value of `fieldId` on the triggering ticket. */
export const workplaceRequestTicketFieldContextKey = (
  fieldId: string
): string => `${TICKET_FIELD_KEY_PREFIX}${fieldId}`;

/**
 * The field id behind a bare `ticket.field.<uuid>` key, or `null` for anything
 * else — a reservation part key included, since
 * {@link workplaceRequestContextKeyReservationPart} reads those instead.
 *
 * The exclusion matters: field ids are UUIDs and carry no dot, so without it a
 * part key answered `"<uuid>.start_time"` and every caller went on to use that
 * as a field id.
 */
export const workplaceRequestContextKeyFieldId = (
  key: string
): string | null => {
  if (!key.startsWith(TICKET_FIELD_KEY_PREFIX)) return null;
  const fieldId = key.slice(TICKET_FIELD_KEY_PREFIX.length);
  return fieldId && !fieldId.includes(".") ? fieldId : null;
};

/**
 * The parts of a `reservation` answer a workflow can read, each published by
 * the approval trigger under the field's own key as
 * `ticket.field.<uuid>.<part>`.
 *
 * `dates` and `date` are both published whenever the answer names at least one
 * day. The rest depend on the field's config: `attendees` on
 * `attendees_enabled`, the three `recurrence*` parts on `recurrence_enabled`.
 */
export const WORKPLACE_REQUEST_RESERVATION_CONTEXT_PARTS = {
  /** Every day the answer names, as a date list. */
  dates: "dates",
  /**
   * The earliest day alone. Never bind a reservation rule's days to this: core
   * rejects the save, because the rule would silently book one day out of many
   * the moment the field turns `multi_day_enabled` on.
   */
  date: "date",
  startTime: "start_time",
  endTime: "end_time",
  attendees: "attendees",
  recurrenceRRule: "recurrence_rrule",
  recurrenceStartDate: "recurrence_start_date",
  recurrenceEndDate: "recurrence_end_date"
} as const;

export type WorkplaceRequestReservationContextPart =
  (typeof WORKPLACE_REQUEST_RESERVATION_CONTEXT_PARTS)[keyof typeof WORKPLACE_REQUEST_RESERVATION_CONTEXT_PARTS];

const RESERVATION_CONTEXT_PARTS: readonly string[] = Object.values(
  WORKPLACE_REQUEST_RESERVATION_CONTEXT_PARTS
);

export const isWorkplaceRequestReservationContextPart = (
  value: string
): value is WorkplaceRequestReservationContextPart =>
  RESERVATION_CONTEXT_PARTS.includes(value);

/** The context key that yields one part of reservation field `fieldId`. */
export const workplaceRequestReservationContextKey = (
  fieldId: string,
  part: WorkplaceRequestReservationContextPart
): string => `${workplaceRequestTicketFieldContextKey(fieldId)}.${part}`;

/**
 * The field and part behind a `ticket.field.<uuid>.<part>` key, or `null` when
 * the key is anything else — a bare field key included, which
 * {@link workplaceRequestContextKeyFieldId} reads instead.
 */
export const workplaceRequestContextKeyReservationPart = (
  key: string
): {
  fieldId: string;
  part: WorkplaceRequestReservationContextPart;
} | null => {
  if (!key.startsWith(TICKET_FIELD_KEY_PREFIX)) return null;
  const rest = key.slice(TICKET_FIELD_KEY_PREFIX.length);
  const separator = rest.indexOf(".");
  if (separator <= 0) return null;
  const part = rest.slice(separator + 1);
  return isWorkplaceRequestReservationContextPart(part)
    ? { fieldId: rest.slice(0, separator), part }
    : null;
};

/**
 * How an action input is filled: a concrete entity picked at configuration time,
 * or a context key read off the ticket when the workflow runs.
 */
export type WorkplaceRequestWorkflowValueSelector =
  | { source: "fixed"; value: string }
  | { source: "context"; key: string };

/**
 * Defers an action to a specific instant. Stored under the reserved `run_at` key
 * of any action config; without it the action runs as soon as the trigger fires.
 *
 * `time` (`HH:MM`) always overrides the time of day, `timezone` (IANA name) always
 * overrides the zone. A resolved instant already in the past runs immediately.
 */
export type WorkplaceRequestWorkflowRunAt = (
  | {
      source: "fixed";
      /** A date (`2026-09-01`) or an RFC3339 instant. */
      at: string;
    }
  | {
      source: "context";
      /** A context key yielding a date — in practice a `date` field on the type. */
      key: string;
    }
) & {
  time?: string;
  timezone?: string;
};

/**
 * How a child-ticket field gets its value when the workflow runs:
 * - `copy_parent_field`: copy from a field on the parent ticket (`parent_field_id`).
 * - `fixed_value`: a fixed value chosen at configuration time (`value`).
 * - `empty`: leave empty (only valid for optional fields).
 */
export type WorkplaceRequestWorkflowMappingSource =
  | "copy_parent_field"
  | "fixed_value"
  | "empty";

export type WorkplaceRequestWorkflowFieldMapping = {
  target_field_id: string;
  source: WorkplaceRequestWorkflowMappingSource;
  /** Present when `source` is `copy_parent_field`. */
  parent_field_id?: string;
  /** Present when `source` is `fixed_value`. */
  value?: unknown;
};

export type WorkplaceRequestWorkflowChildSpec = {
  target_ticket_type_id: string;
  name?: string;
  field_mappings: WorkplaceRequestWorkflowFieldMapping[];
};

/** Config payload for the `tickets:create_child_ticket` action. */
export type WorkplaceRequestCreateChildTicketConfig = {
  children: WorkplaceRequestWorkflowChildSpec[];
  run_at?: WorkplaceRequestWorkflowRunAt;
};

/**
 * What becomes of the desk(s) a person is released from when a `resources:`
 * action runs. Assigning is a move in core — taking the new desk gives up the
 * old one — and this says whether the old one is then left alone, handed back
 * to everyone, or taken out of service.
 *
 * For the two releasing actions the released desk *is* the desk the rule names,
 * so this decides what happens to that one.
 */
export const WORKPLACE_REQUEST_RELEASED_RESOURCE_ACTIONS = {
  /** Leave the released desk exactly as it is. The default. */
  none: "none",
  /** Open it for anyone to book, dropping any dedication. */
  makeOpen: "make_open",
  /** Mark it unavailable, so nobody can book it. */
  makeUnavailable: "make_unavailable"
} as const;

export type WorkplaceRequestReleasedResourceAction =
  (typeof WORKPLACE_REQUEST_RELEASED_RESOURCE_ACTIONS)[keyof typeof WORKPLACE_REQUEST_RELEASED_RESOURCE_ACTIONS];

/**
 * Config shared by all four `resources:` actions. Both slots are mandatory, and
 * the backend rejects a config where they name the same context key.
 *
 * Note this applies to the releasing actions too: core removes the hold on one
 * named desk, so there is no "release every desk this person holds" to express.
 */
export type WorkplaceRequestResourceAssignmentConfig = {
  user: WorkplaceRequestWorkflowValueSelector;
  resource: WorkplaceRequestWorkflowValueSelector;
  /** Omitted means `none` — core treats an absent value as "leave it alone". */
  released_resource_action?: WorkplaceRequestReleasedResourceAction;
  run_at?: WorkplaceRequestWorkflowRunAt;
};

/**
 * Config for the `webhooks:send` action. The backend rejects it unless the
 * organization has a webhook signing secret and the URL passes its checks
 * (HTTPS, not a private address, …).
 */
export type WorkplaceRequestSendWebhookConfig = {
  url: string;
  run_at?: WorkplaceRequestWorkflowRunAt;
};

/**
 * Config for the `reservations:create` action: which days to book, the time of
 * day they share, and where and for whom.
 *
 * Every day named by `dates` becomes its own reservation — at most 15, none
 * overlapping once the buffer is applied — unless `recurrence` resolves, in
 * which case one recurring series is booked instead. A recurrence and several
 * days cannot be combined.
 *
 * Title, description, attendees and online meeting only apply to rooms: core
 * drops them for a desk and reports them in the run's `dropped_fields`. The
 * text slots read `short_text` / `long_text` fields as typed, and
 * `single_select` / `multi_select` fields as their chosen option labels.
 * `buffer` needs both halves (whole minutes, 0..2880) when present. There is
 * no creator slot — the creator is always whoever caused the trigger to fire.
 * Approvers may replace any resolved value through `workflow_overrides`.
 */
export type WorkplaceRequestCreateReservationConfig = {
  /**
   * The days to book. Fixed value: a single `YYYY-MM-DD`. Context: a
   * reservation field's `dates` part, or a plain `date` field — one day
   * satisfies a date list. A reservation field's `date` part is rejected when
   * the workflow is saved; see
   * {@link WORKPLACE_REQUEST_RESERVATION_CONTEXT_PARTS}.
   */
  dates: WorkplaceRequestWorkflowValueSelector;
  /**
   * Fixed value: `HH:MM` (24h). Context: a `time` field, or a reservation
   * field's `start_time` part. Shared by every day, and read in the ticket's
   * office timezone.
   */
  start_time: WorkplaceRequestWorkflowValueSelector;
  /** As `start_time`, and after it when both are fixed. */
  end_time: WorkplaceRequestWorkflowValueSelector;
  resource: WorkplaceRequestWorkflowValueSelector;
  organizer: WorkplaceRequestWorkflowValueSelector;
  title?: WorkplaceRequestWorkflowValueSelector;
  /**
   * Every entry that resolves to a non-empty string, joined with newlines in
   * order; an entry that resolves to nothing adds nothing. Core returns the
   * single selector a workflow was saved with before this became a list, so
   * both shapes come back — new saves always send an array.
   */
  description?:
    | WorkplaceRequestWorkflowValueSelector[]
    | WorkplaceRequestWorkflowValueSelector;
  /**
   * Each entry names a single user, except a reservation field's `attendees`
   * part, which expands into everyone the submitter invited. A fixed entry is
   * always a user id, never an email. An entry whose context key the ticket
   * never published resolves to nothing and is dropped, rather than failing
   * the run.
   */
  attendees?: WorkplaceRequestWorkflowValueSelector[];
  /**
   * Attendees always invited, on top of whatever `attendees` resolves to. A
   * user or address already resolved as an attendee is not repeated. At most
   * 500, checked when the workflow is saved: a `user_id` has to be an existing
   * user, while an `email` matching nobody is simply booked as a guest.
   *
   * An approval-time `attendees` override replaces the whole list, these
   * included — so an approver who edits the attendees books without them.
   */
  default_attendees?: WorkplaceRequestReservationAttendeeInput[];
  /** Fixed value: `"true"` | `"false"`; context: a `toggle` field. */
  online_meeting?: WorkplaceRequestWorkflowValueSelector;
  buffer?: {
    before: WorkplaceRequestWorkflowValueSelector;
    after: WorkplaceRequestWorkflowValueSelector;
  };
  /**
   * Books a recurring series instead of one reservation per day. Omit it
   * entirely for a plain booking. Each side is sourced independently, so a
   * fixed rule can be combined with dates read off the ticket; when the rrule
   * or the start date is context-sourced and the ticket published no value for
   * it, the run books a single reservation rather than failing.
   */
  recurrence?: {
    /**
     * Fixed value: an RRULE such as `FREQ=WEEKLY;BYDAY=TU`, parsed when the
     * workflow is saved. Context: a reservation field's `recurrence_rrule` part.
     */
    rrule: WorkplaceRequestWorkflowValueSelector;
    /** Fixed value: `YYYY-MM-DD`. */
    start_date: WorkplaceRequestWorkflowValueSelector;
    /** Fixed value: `YYYY-MM-DD`. Omit it for an open-ended series. */
    end_date?: WorkplaceRequestWorkflowValueSelector;
  };
  run_at?: WorkplaceRequestWorkflowRunAt;
};

/**
 * Any action config. Narrow it with the workflow's `action` rather than by
 * probing keys — a future action may reuse the same shape.
 */
export type WorkplaceRequestWorkflowConfig =
  | WorkplaceRequestCreateChildTicketConfig
  | WorkplaceRequestResourceAssignmentConfig
  | WorkplaceRequestSendWebhookConfig
  | WorkplaceRequestCreateReservationConfig;

export type WorkplaceRequestWorkflow = {
  id: string;
  /** Display name, or `null` when not set. */
  name: string | null;
  /** A `string` fallback keeps actions added by core forward-readable. */
  trigger: WorkplaceRequestWorkflowTrigger | string;
  action: WorkplaceRequestWorkflowAction | string;
  config: WorkplaceRequestWorkflowConfig;
  enabled: boolean;
  /** Why the workflow was auto-disabled (e.g. its config became invalid), or `null`. */
  disabled_reason: string | null;
};

export type CreateWorkplaceRequestWorkflowParams = {
  name?: string;
  trigger: WorkplaceRequestWorkflowTrigger | string;
  action: WorkplaceRequestWorkflowAction | string;
  config: WorkplaceRequestWorkflowConfig;
};

export type UpdateWorkplaceRequestWorkflowParams = {
  name?: string;
  trigger?: WorkplaceRequestWorkflowTrigger | string;
  action?: WorkplaceRequestWorkflowAction | string;
  config?: WorkplaceRequestWorkflowConfig;
  /** Explicitly set to re-enable a workflow that was auto-disabled. */
  enabled?: boolean;
};

/**
 * A sub-status (ticket type status) configured for a workplace request type.
 */
export type WorkplaceRequestTypeStatus = {
  id: string;
  ticket_type_id: string;
  name: string;
  description: string;
  base_status: WorkplaceRequestBaseStatus;
  order: number;
  created: string;
  updated: string;
};

export type WorkplaceRequestTypeElement = {
  id: string;
  name: string;
  description?: string;
  llm_context?: string;
  /**
   * Whether this type files a ticket at all. Backfilled to `default` server-side,
   * so it is always present. `information` types are deliberately *not* filtered
   * out of the type lists — they are signposts, and employees are meant to find
   * them in the picker.
   */
  kind: WorkplaceRequestTypeKind;
  /**
   * Sanitized HTML shown to the employee in place of the form, up to 20 000
   * characters. Only ever set on an `information` type.
   */
  instructions?: string;
  category_id: string;
  office_ids: string[];
  has_office_restrictions: boolean;
  /**
   * Resources (e.g. meeting rooms) this type is restricted to. Evaluated as an
   * allow-list *alongside* `office_ids`, not nested inside it: a ticket passes
   * when its resource is listed here or when its office is allowed. Empty with
   * `has_resource_restrictions === false` means every resource is allowed.
   */
  resource_ids: string[];
  /**
   * Whether the type has any resource restriction at all. True even when
   * `resource_ids` is empty because the caller cannot see the restricted
   * resources, mirroring `has_office_restrictions`.
   */
  has_resource_restrictions: boolean;
  /**
   * Divisions this type is restricted to, filtered to the divisions the
   * requesting user can access. Restricting by division is an *alternative* to
   * listing offices, so a type scoped this way carries
   * `has_office_restrictions === true` with the matching offices absent from
   * {@link office_ids} — resolving which offices a division holds needs a
   * separate call.
   */
  division_ids?: string[];
  // Responses embed full user objects (core #1948); create/update still take
  // IDs — see the params types below.
  default_auto_assignee: WorkplaceRequestUser | null;
  office_auto_assignees: Record<string, WorkplaceRequestUser>;
  /**
   * Per-division auto-assignee override map (division_id -> user). Backed by
   * core but not yet surfaced in the UI; optional until the division
   * assignment UI lands.
   */
  division_auto_assignees?: Record<string, WorkplaceRequestUser>;
  statuses: WorkplaceRequestTypeStatus[];
  fields: WorkplaceRequestTypeField[];
  /**
   * Presentational icon identifier (lucide-style name). Backed by the API,
   * which stores it as `{ identifier }`; the client maps it to/from a plain
   * string. `null` when no icon is set.
   */
  icon: string | null;
  /**
   * When true, new requests of this type start pending workplace-admin approval
   * before work proceeds.
   */
  requires_approval: boolean;
  /**
   * When true, an approved request of this type is closed straight away instead
   * of staying open. Only has an effect while `requires_approval` is true.
   */
  close_on_approval: boolean;
  /**
   * When true, requests of this type must carry a location (core
   * `requires_location`) — by creation time, unless `require_location_at_approval`
   * defers that to approval time instead.
   */
  requires_location: boolean;
  /**
   * When true (and `requires_location` is also true), a request of this type
   * may be created without a location, but must have one before it can be
   * approved. Has no effect unless `requires_location` is true.
   */
  require_location_at_approval: boolean;
  /**
   * When true, requests of this type carry no description: the backend neither
   * stores nor returns one, so the field is hidden everywhere it would be
   * filled in or displayed.
   */
  hide_description: boolean;
  /**
   * Mustache template the ticket's title is rendered from when set, in which
   * case submitters are never asked for one. Variables are the workflow context
   * keys (`{{ ticket.field.<uuid> }}`, `{{ ticket.office }}`, …); see
   * `titleTemplateVariables` in `@gettactic/helpers` for the keys a type
   * offers. `null` (or absent, on a type saved before the field existed) means
   * the submitter types the title.
   */
  title_template?: string | null;
  /**
   * Whether a schedule/reservation may (or must) be linked to requests of this
   * type. `required` is surfaced in the editor as "Requires a reservation".
   */
  schedule_requirement?: WorkplaceRequestScheduleRequirement;
  /**
   * Workflows owned by this ticket type, embedded on the list endpoint.
   * Real API (managed via the workflow CRUD methods). `undefined` until the
   * backend includes it; treat as an empty list.
   */
  workflows?: WorkplaceRequestWorkflow[];
  // TODO(workplace-requests): the following are mock-only fields overlaid by the
  // client (localStorage) until the backend adds them. See
  // docs/workplace-requests-backend-api-gaps.md §1.3 (visibility) and §5
  // (notifications). Linked workflows are now backed by the real `workflows`
  // field above.
  visibility_category_ids?: string[];
  notification_rules?: WorkplaceRequestNotificationRule[];
  /** Present only on the admin list endpoint (`/v1/tickets/types`). */
  in_use?: boolean;
  /**
   * TEMPORARY backwards-compat shim. Present only on the admin list endpoint and
   * reflects the default (no-office) assigned users of the type's category.
   */
  assigned_users?: WorkplaceRequestUser[];
};

export type WorkplaceRequestTypesResponse = {
  cursor: string | null;
  elements: WorkplaceRequestTypeElement[];
};

/**
 * Editing a ticket type re-validates every workflow that owns or references it;
 * the ones that became invalid come back auto-disabled so the caller can warn
 * the admin straight away. See docs/workplace-requests-backend-api-gaps.md §4.5.
 */
export type WorkplaceRequestTypeUpdateResponse = WorkplaceRequestTypeElement & {
  disabled_workflows?: WorkplaceRequestWorkflow[];
};

/** Deleting a ticket type returns only the workflows it auto-disabled. */
export type WorkplaceRequestTypeDeleteResponse = {
  disabled_workflows?: WorkplaceRequestWorkflow[];
};

export type WorkplaceRequestTypesParams = {
  query?: string;
  user_id?: string[];
  /**
   * Only return the types the requesting user fully manages. The API rejects it
   * alongside {@link user_id}. Organization owners manage every type, so this
   * narrows nothing for them.
   */
  managed?: boolean;
  isAdmin?: boolean;
  /**
   * Only return types usable at this resource, applying the resource-level location
   * rules. Takes precedence over {@link office_id}, which the API rejects alongside it.
   */
  resource_id?: string;
  /**
   * Only return types usable in this office, division-scoped types resolved to their
   * offices. Ignored when {@link resource_id} is set.
   */
  office_id?: string;
};

/**
 * The optional attributes of a field / option are tri-state on the wire: omit
 * the key to leave the stored value untouched, and send `null` to clear it. A
 * value is only ever overwritten when it is explicitly sent.
 */
export type WorkplaceRequestTypeFieldOptionInput = {
  id?: string;
  label: string;
  /** Secondary line shown under the option's label. `null` clears it. */
  description?: string | null;
  /**
   * Private storage path returned by the option image upload endpoint. Omit to
   * leave the stored image untouched; send `null` to delete it.
   */
  image_path?: string | null;
};

export type WorkplaceRequestTypeFieldInput = {
  id?: string;
  name: string;
  /** `null` clears the field's description. */
  description?: string | null;
  field_type: WorkplaceRequestTypeFieldType;
  is_required: boolean;
  options?: WorkplaceRequestTypeFieldOptionInput[];
  /**
   * Tri-state, like the option fields above: omitting it (or sending `null`)
   * leaves the stored configuration untouched, and an empty object clears it.
   * A `reservation` field should always send its `reservation` entry, since
   * that is the only way to change a switch.
   */
  config?: WorkplaceRequestTypeFieldConfig | null;
};

/**
 * Mock-only ticket type fields (visibility, notifications) accepted by
 * create/update and persisted by the client's mock overlay.
 * TODO(workplace-requests): remove once the backend supports these. See
 * docs/workplace-requests-backend-api-gaps.md §1.3, §5.
 */
export type WorkplaceRequestTypeMockFields = {
  visibility_category_ids?: string[];
  notification_rules?: WorkplaceRequestNotificationRule[];
};

/**
 * Creating an `information` type is a **400** if the payload also carries
 * `fields`, `requires_approval`, `requires_location`, an auto-assignee, or a
 * `schedule_requirement` other than `optional`: none of them apply to a type
 * that never files a ticket.
 */
export type CreateWorkplaceRequestTypeParams =
  WorkplaceRequestTypeMockFields & {
    name: string;
    description?: string;
    llm_context?: string;
    category_id?: string;
    office_ids?: string[];
    /** Resource-level allow-list; see `WorkplaceRequestTypeElement.resource_ids`. */
    resource_ids?: string[];
    fields?: WorkplaceRequestTypeFieldInput[];
    default_auto_assignee?: string;
    office_auto_assignees?: Record<string, string>;
    icon?: string | null;
    requires_approval?: boolean;
    close_on_approval?: boolean;
    requires_location?: boolean;
    require_location_at_approval?: boolean;
    hide_description?: boolean;
    /** Rejected on an `information` type. See `WorkplaceRequestTypeElement`. */
    title_template?: string;
    schedule_requirement?: WorkplaceRequestScheduleRequirement;
    /** Omit for an ordinary form type. */
    kind?: WorkplaceRequestTypeKind;
    /** Accepted only together with `kind: "information"`. */
    instructions?: string;
  };

/**
 * ⚠️ An update that leaves the type an `information` one is validated on the
 * **presence** of a key, not on its value. The backend returns 400 if the
 * payload contains any of `fields`, `requires_approval`, `requires_location`,
 * `schedule_requirement`, `default_auto_assignee`, `office_auto_assignees`,
 * `division_auto_assignees` or `resource_auto_assignees` — even when the value
 * is unchanged, and even when it is explicitly `null`.
 *
 * So an `information` type needs a *reduced* payload carrying only `name`,
 * `description`, `category_id`, `icon`, `office_ids`, `resource_ids`,
 * `view_only_category_ids`, `hide_description`, `llm_context`,
 * `instructions` and a `null` `title_template`. See `toUpdateParams` in the
 * request-type editor's draft.
 *
 * The check runs against the kind the request *results in*, so the one payload
 * that may carry both is the migration to a form: `kind: "default"` alongside
 * `fields`. Conversely a type that is (or becomes) `default` is rejected for a
 * non-empty `instructions`.
 */
export type UpdateWorkplaceRequestTypeParams =
  WorkplaceRequestTypeMockFields & {
    name?: string;
    description?: string;
    llm_context?: string;
    category_id?: string;
    office_ids?: string[];
    /** Resource-level allow-list; see `WorkplaceRequestTypeElement.resource_ids`. */
    resource_ids?: string[];
    fields?: WorkplaceRequestTypeFieldInput[];
    // `null` clears the global default assignee.
    default_auto_assignee?: string | null;
    office_auto_assignees?: Record<string, string>;
    icon?: string | null;
    requires_approval?: boolean;
    close_on_approval?: boolean;
    requires_location?: boolean;
    require_location_at_approval?: boolean;
    hide_description?: boolean;
    /**
     * `null` clears the template so submitters type the title again; omitting
     * it leaves the stored one in place. Core validates a non-null value
     * against the fields as they will be after this same save, answering
     * `title_template_missing_field` or `invalid_title_template` (400).
     * Setting one on an `information` type is rejected, clearing one is not.
     */
    title_template?: string | null;
    schedule_requirement?: WorkplaceRequestScheduleRequirement;
    /**
     * Only `"default"` is accepted, and only on a type that is currently
     * `information`. Sending `"information"` is a 400 whatever the type's
     * current kind.
     */
    kind?: "default";
    instructions?: string;
  };

export type CreateWorkplaceRequestTypeStatusParams = {
  name: string;
  description?: string;
  base_status: WorkplaceRequestBaseStatus;
};

export type UpdateWorkplaceRequestTypeStatusParams = {
  name?: string;
  description?: string;
};

export type WorkplaceRequestTypeStatusPosition = {
  id: string;
  order: number;
};

export type ReorderWorkplaceRequestTypeStatusesParams = {
  base_status: WorkplaceRequestBaseStatus;
  statuses: WorkplaceRequestTypeStatusPosition[];
};
